import { CSSProperties, useLayoutEffect, useRef } from 'react'

import { HudStyle } from '../behaviors'

/** HUD 要素のビューポート（relative 親）内での配置。 */
export interface HudBounds {
  readonly top: number
  readonly bottom: number
  readonly left: number
  readonly right: number
}

/**
 * スロット名 → HUD 表示内容。複数スロットを同時に重ねて表示できる
 * （実機の複数 ScreenGui 相当。例: RJF11 のカウントと終了表示の併記）。
 */
export type HudSlots = Record<string, { text: string; style?: HudStyle }>

export interface HudOverlayProps {
  /** スロット名 → テキストとスタイル。空オブジェクトで非表示。 */
  huds: HudSlots
  /**
   * HUD の実測配置の通知。出力ログが帯と重ならない位置を計算するために
   * 使う（文言の折り返しで帯の高さが変わるため、スタイルでなく実測で渡す）。
   * 複数スロット時は全バッジの外接矩形（union）を渡す。非表示時は null。
   */
  onBoundsChange?: (bounds: HudBounds | null) => void
}

/**
 * HudStyle から実機 StarterGui の TextLabel デザイン（背景・文字・枠色・
 * 帯/バッジ）を再現したスタイルを組む。style 無指定はホスト既定の白バッジ。
 */
const badgeStyle = (style?: HudStyle): CSSProperties =>
  style ?
    {
      position: 'absolute',
      top: style.top ?? 0,
      ...(style.wide ? { left: 0, right: 0 }
      : style.left !== undefined ? { left: style.left }
      : style.right !== undefined ? { right: style.right }
      : { left: '50%', transform: 'translateX(-50%)' }),
      background: style.background,
      color: style.color,
      border:
        style.border ?
          `${style.borderWidth ?? 2}px solid ${style.border}`
        : undefined,
      padding: style.wide ? '6px 18px' : '4px 32px',
      // 実機の TextLabel は TextScaled で高さ 50px の帯いっぱいに文字が出るが、
      // プレビューでは文字が大きすぎたため一律 30% 縮小（26 → 18）。
      fontSize: 18,
      fontWeight: 700,
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      pointerEvents: 'none',
    }
  : {
      position: 'absolute',
      top: 8,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(255, 255, 255, 0.85)',
      color: '#1A1A2E',
      borderRadius: 8,
      padding: '4px 18px',
      // 既定バッジも一律 30% 縮小（20 → 14）。
      fontSize: 14,
      fontWeight: 700,
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      pointerEvents: 'none',
    }

/**
 * ミッション GUI（ScreenGui/TextLabel 相当）。タイマー・カウント・
 * クリアメッセージ等を実機同様に上部へ重ねる。スロットごとに 1 つの帯/
 * バッジを描画し、複数スロットを同時表示できる。
 */
export function HudOverlay({ huds, onBoundsChange }: HudOverlayProps) {
  const badgeRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const onBoundsChangeRef = useRef(onBoundsChange)
  onBoundsChangeRef.current = onBoundsChange

  const entries = Object.entries(huds)

  // 表示内容・スタイルの変化と、フォント読込や折り返しによる遅延サイズ
  // 変化（ResizeObserver）の両方で実測値を通知する。複数バッジは外接矩形
  // （union）にまとめる。offset 系は position: relative な親（ビューポート）基準。
  useLayoutEffect(() => {
    const notify = () => {
      // OutputLog の帯回避は上端の wide 帯だけを避ければよい。左上の小バッジ等の
      // 非 wide スロットは帯ではないので bounds に含めない（含めると、wide 帯より
      // 下に出る非 wide バッジで union 下端が伸び、ログが不要に下がる）。wide
      // スロットの外接矩形（union）を返し、wide が無ければ null。
      let top = Infinity
      let bottom = -Infinity
      let left = Infinity
      let right = -Infinity
      let hasWide = false
      for (const [slot, hud] of Object.entries(huds)) {
        if (!hud.style?.wide) {
          continue
        }
        const element = badgeRefs.current.get(slot)
        if (!element) {
          continue
        }
        hasWide = true
        top = Math.min(top, element.offsetTop)
        bottom = Math.max(bottom, element.offsetTop + element.offsetHeight)
        left = Math.min(left, element.offsetLeft)
        right = Math.max(right, element.offsetLeft + element.offsetWidth)
      }
      onBoundsChangeRef.current?.(hasWide ? { top, bottom, left, right } : null)
    }
    notify()
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        onBoundsChangeRef.current?.(null)
      }
    }
    const observer = new ResizeObserver(notify)
    for (const element of badgeRefs.current.values()) {
      observer.observe(element)
    }
    return () => {
      observer.disconnect()
      onBoundsChangeRef.current?.(null)
    }
  }, [huds])

  if (entries.length === 0) {
    return null
  }
  return (
    <>
      {entries.map(([key, hud]) => (
        <div
          key={key}
          ref={(element) => {
            if (element) {
              badgeRefs.current.set(key, element)
            } else {
              badgeRefs.current.delete(key)
            }
          }}
          aria-label="ミッションひょうじ"
          style={badgeStyle(hud.style)}
        >
          {hud.text}
        </div>
      ))}
    </>
  )
}
